import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client.js";

export interface TagScheduleDto {
  tag: string;
  cron: string;
  updatedAt: string;
}

export interface ConflictItemDto {
  id: string;
  displayName: string;
  otherTagsWithSchedule: string[];
}

export function useTagSchedules() {
  return useQuery({
    queryKey: ["tag-schedules"],
    queryFn: () => api<{ schedules: TagScheduleDto[] }>("/api/tag-schedules"),
    select: (r) => r.schedules,
    staleTime: 30 * 1000,
  });
}

export function useUpsertTagSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tag, cron }: { tag: string; cron: string }) =>
      api<{ schedule: TagScheduleDto }>(`/api/tag-schedules/${encodeURIComponent(tag)}`, {
        method: "PUT",
        body: { cron },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tag-schedules"] });
      // Items могли поменять effective cron — инвалидируем.
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useDeleteTagSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tag: string) =>
      api<void>(`/api/tag-schedules/${encodeURIComponent(tag)}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tag-schedules"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

export function useTagScheduleConflicts(tag: string | null) {
  return useQuery({
    queryKey: ["tag-schedule-conflicts", tag],
    queryFn: () =>
      api<{ items: ConflictItemDto[] }>(
        `/api/tag-schedules/${encodeURIComponent(tag!)}/conflicts`,
      ),
    enabled: !!tag,
    select: (r) => r.items,
  });
}
